import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { AuthState } from './types';

/**
 * Store de autenticação com persistência automática no localStorage
 * 
 * O Zustand persist salva automaticamente no localStorage e restaura no refresh.
 * Isso funciona tanto para web quanto para mobile (webview).
 */
export const useAuthStore = create<AuthState>()(
	persist(
		(set) => ({
			squadToken: null,
			jwtTokens: null,
			exchangeCodeToken: null,
			authStatus: 'pending' as const,
			setToken: (squadToken) => set({ squadToken }),
			setJwtTokens: (jwtTokens) => set({ jwtTokens }),
			setExchangeCodeToken: (exchangeCodeToken) => set({ exchangeCodeToken }),
			setAuthStatus: (authStatus) => set({ authStatus }),
			setExternalUser: ({ email, userName, participantId, xmppUser, xmppPw, xmppDomain, temporaryUserId, expiresAt }) => set((state) => ({
				squadToken: {
					access_token: '',
					domain: xmppDomain || state.squadToken?.domain || '',
					baseUrl: '',
					email,
					user_name: userName,
					expires_in: 0,
					user_id: participantId || state.squadToken?.user_id || '',
					isExternal: true,
					xmppUser: xmppUser || state.squadToken?.xmppUser,
					xmppPw: xmppPw || state.squadToken?.xmppPw,
					xmppDomain: xmppDomain || state.squadToken?.xmppDomain,
					temporaryUserId: temporaryUserId || state.squadToken?.temporaryUserId,
					expiresAt: expiresAt || state.squadToken?.expiresAt,
				},
				authStatus: 'external' as const,
			})),
			clearToken: () => set({ squadToken: null }),
			clearAll: () => set({ squadToken: null, jwtTokens: null, exchangeCodeToken: null, authStatus: 'pending' as const }),
		}),
		{
			name: 'meet-auth-storage',
			storage: createJSONStorage(() => localStorage),
			partialize: (state) => ({
				squadToken: state.squadToken,
				jwtTokens: state.jwtTokens,
				exchangeCodeToken: state.exchangeCodeToken,
			}),
			version: 1,
		},
	),
);

/**
 * Acesso direto ao estado do Zustand (para uso em interceptors onde não podemos usar hooks)
 * Exemplo: useAuthStore.getState().jwtTokens?.accessToken
 */
export const getStoredAccessToken = (): string | null => {
	return useAuthStore.getState().jwtTokens?.accessToken ?? null;
};

export const getStoredRefreshToken = (): string | null => {
	return useAuthStore.getState().jwtTokens?.refreshToken ?? null;
};

export const getStoredExchangeCodeToken = (): string | null => {
	return useAuthStore.getState().exchangeCodeToken ?? null;
};
